import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import type { PushDismissDto, PushSubscribeDto } from "shared/dto/push";
import { apiClient } from "@/lib/ApiClient";
import { detectPlatform } from "@/lib/Platform";
import { registerServiceWorker } from "@/lib/RegisterSw";

// @capacitor/local-notifications·push-notifications 를 쓰는 네이티브 헬퍼는
// 아래 isNativeMobile 브랜치에서만 동적 import 한다 — 웹/데스크톱(첫 화면 포함)이
// capacitor 플러그인을 entry(초기) 청크로 끌어오지 않게 하기 위함이다.

// 로그인 후 현재 기기/브라우저의 푸시 구독을 서버(현재 세션 행)에 등록한다.
// VAPID 공개키는 빌드 타임 env 로 주입한다(비밀키는 서버 전용).
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

// VAPID 공개키(base64url)를 pushManager.subscribe 가 요구하는 Uint8Array 로 변환.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** POST /api/push/subscribe */
function useSubscribePushMutation() {
  return useMutation({
    mutationFn: async (json: PushSubscribeDto) => {
      const res = await apiClient.api.push.subscribe.$post({ json });
      // fetch() 는 401/400 같은 HTTP 에러에도 절대 throw 하지 않는다. 여기서
      // res.ok 를 확인하지 않으면 브라우저는 구독에 성공했는데 서버 저장만
      // 조용히 실패하는 상태가 영원히 감지되지 않는다.
      const isResNotOk = !res.ok;
      if (isResNotOk) {
        const body = await res.text().catch(() => "");
        throw new Error(`구독 저장 실패 (${res.status}) ${body}`);
      }
    },
  });
}

/** DELETE /api/push/subscribe */
function useUnsubscribePushMutation() {
  return useMutation({
    mutationFn: async () => {
      await apiClient.api.push.subscribe.$delete();
    },
  });
}

/** POST /api/push/dismiss */
function useDismissPushMutation() {
  return useMutation({
    mutationFn: async (json: PushDismissDto) => {
      await apiClient.api.push.dismiss.$post({ json });
    },
  });
}

/**
 * 지원 여부·권한을 확인하고 웹 푸시 구독을 만들어 서버에 등록한다.
 * 조용히 실패(미지원/권한 거부/키 없음)해도 앱 흐름은 막지 않는다.
 */
export function useRegisterPushSubscription() {
  const { mutateAsync: subscribe } = useSubscribePushMutation();

  return useCallback(async () => {
    const platform = detectPlatform();

    // web/desktop 은 동일한 Chromium 기반 PushManager/ServiceWorker 경로를 쓴다.
    // Capacitor 네이티브(android/ios)는 FCM 등록 토큰을 {token} 형태로 등록한다 —
    // 백그라운드/종료 상태 알림은 FCM 트레이가, 포그라운드 알림은 realtime
    // (room=userId, use-realtime.ts) + 로컬 알림이 담당한다.
    const isNotWeb = platform !== "web";
    const isNotDesktop = platform !== "desktop";
    const isNativeMobile = isNotWeb && isNotDesktop;
    if (isNativeMobile) {
      const [{ ensureNativeNotificationPermission }, { registerNativePushToken }] =
        await Promise.all([
          import("@/lib/NativeNotifications"),
          import("@/lib/NativePush"),
        ]);
      try {
        await ensureNativeNotificationPermission();
      } catch (err) {
        console.warn("[push] 네이티브 알림 권한 요청 실패", err);
      }
      try {
        const token = await registerNativePushToken();
        // FCM 토큰을 얻지 못했는지(권한 거부/Play 서비스 없음/타임아웃)
        const isTokenUnavailable = !token;
        if (isTokenUnavailable) return;
        await subscribe({ platform, pushSubscription: { token } });
      } catch (err) {
        console.warn("[push] FCM 토큰 등록 실패", err);
      }
      return;
    }

    const isPushUnsupported =
      typeof navigator === "undefined" ||
      !("serviceWorker" in navigator) ||
      !("PushManager" in window) ||
      !("Notification" in window);
    if (isPushUnsupported) return;

    const isPermissionDenied = Notification.permission === "denied";
    if (isPermissionDenied) return;

    try {
      const permission =
        Notification.permission === "granted"
          ? "granted"
          : await Notification.requestPermission();
      const isPermissionNotGranted = permission !== "granted";
      if (isPermissionNotGranted) return;

      const registration = await registerServiceWorker();
      if (!registration) return;
      const existing = await registration.pushManager.getSubscription();
      const subscription =
        existing ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        }));

      await subscribe({ platform, pushSubscription: subscription.toJSON() as Record<string, unknown> });
    } catch (err) {
      console.warn("[push] 구독 등록 실패", err);
    }
  }, [subscribe]);
}

/** 로그아웃 전 현재 세션의 구독을 서버에서 지운다. 실패해도 로그아웃 흐름은 막지 않는다. */
export function useUnregisterPushSubscription() {
  const { mutateAsync: unsubscribe } = useUnsubscribePushMutation();

  return useCallback(async () => {
    try {
      await unsubscribe();
    } catch (err) {
      console.warn("[push] 구독 해제 실패", err);
    }
  }, [unsubscribe]);
}

/**
 * 이 기기에서 해당 알림(tag)을 확인했으니 정리한다: (1) 이 기기에 아직 떠 있는 같은
 * 알림을 닫고(알림을 클릭하지 않고 앱에서 직접 task 를 연 경우), (2) 다른 기기에도
 * 같은 알림을 닫으라고 서버에 알린다. 미지원/실패는 조용히 무시(앱 흐름을 막지 않음).
 */
export function useDismissNotification() {
  const { mutateAsync: dismiss } = useDismissPushMutation();

  return useCallback(
    async (tag: string) => {
      try {
        const hasServiceWorker = typeof navigator !== "undefined" && "serviceWorker" in navigator;
        if (hasServiceWorker) {
          const registration = await navigator.serviceWorker.ready;
          const notifications = await registration.getNotifications({ tag });
          for (const notification of notifications) notification.close();
        }
      } catch (err) {
        console.warn("[push] 로컬 알림 정리 실패", err);
      }
      try {
        await dismiss({ tag });
      } catch (err) {
        console.warn("[push] 알림 dismiss 실패", err);
      }
    },
    [dismiss],
  );
}
